import React, { useState, useEffect, useRef } from 'react';
import {
  Search,
  X
} from 'lucide-react';

const WinnerSelector = ({ campaignId, api, selectedWinner, onWinnerSelect, onWinnerClear }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [entries, setEntries] = useState([]);
  const [filteredEntries, setFilteredEntries] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Fetch entries when component mounts or campaignId changes
  useEffect(() => {
    if (campaignId) {
      fetchEntries();
    }
  }, [campaignId]);

  // Filter entries based on search term
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredEntries([]);
      return;
    }

    const filtered = entries.filter(entry => {
      const name = entry.name?.toLowerCase() || '';
      const email = entry.email?.toLowerCase() || '';
      const search = searchTerm.toLowerCase();
      
      return name.includes(search) || email.includes(search);
    });

    setFilteredEntries(filtered.slice(0, 10)); // Limit to 10 results
  }, [searchTerm, entries]);

  // Handle clicks outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target) && 
          inputRef.current && !inputRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchEntries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await api.get(`/admin/campaigns/${campaignId}/entries-for-winner-selection`);
      const entriesData = response.data.entries || [];
      
      setEntries(entriesData);
    } catch (error) {
      console.error('Failed to fetch entries:', error);
      setError('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    setIsOpen(value.length > 0);
  };

  const handleEntrySelect = (entry) => {
    onWinnerSelect(entry);
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleClear = () => {
    onWinnerClear();
    setSearchTerm('');
    setIsOpen(false);
  };

  const handleInputFocus = () => {
    if (searchTerm.length > 0) {
      setIsOpen(true);
    }
  };

  return (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Select Winner
        </label>
        <p className="text-xs text-gray-500 mb-3">
          Search and select one winner from campaign entries
        </p>
        
        <div className="relative" ref={dropdownRef}>
          <div className="flex">
            {/* Search Icon */}
            <div className="flex items-center px-3 bg-gray-50 border border-r-0 border-gray-300 rounded-l-lg">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            
            {/* Input Field */}
            <input
              ref={inputRef}
              type="text"
              value={searchTerm}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              placeholder="Search by name or email..."
              className="flex-1 px-3 py-3 border border-gray-300 focus:ring-2 focus:ring-teal-500 focus:border-teal-500 focus:z-10"
              disabled={loading}
            />
            
            {/* Clear Button */}
            {selectedWinner && (
              <button
                type="button"
                onClick={handleClear}
                className="flex items-center px-3 bg-gray-50 border border-l-0 border-gray-300 rounded-r-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100"
              >
                <X className="h-5 w-5" />
              </button>
            )}
          </div>

          {/* Dropdown */}
          {isOpen && (
            <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto">
              {loading ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  Loading entries...
                </div>
              ) : error ? (
                <div className="px-4 py-3 text-sm text-red-500 text-center">
                  {error}
                </div>
              ) : filteredEntries.length === 0 ? (
                <div className="px-4 py-3 text-sm text-gray-500 text-center">
                  {searchTerm.length > 0 ? 'No entries found' : 'Start typing to search...'}
                </div>
              ) : (
                filteredEntries.map((entry) => (
                  <button
                    key={entry.id}
                    type="button"
                    onClick={() => handleEntrySelect(entry)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="flex flex-col">
                      <div className="font-medium text-gray-900">{entry.name}</div>
                      <div className="text-sm text-gray-500">{entry.email}</div>
                      <div className="text-xs text-gray-400">
                        Entry #{entry.entry_number} • {entry.status}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Winner Display */}
      {selectedWinner && (
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-4">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h4 className="text-sm font-medium text-teal-900 mb-1">Selected Winner</h4>
              <div className="text-lg font-semibold text-teal-800">{selectedWinner.name}</div>
              <div className="text-sm text-teal-600">{selectedWinner.email}</div>
              <div className="text-xs text-teal-500 mt-1">
                Entry #{selectedWinner.entry_number} • {selectedWinner.status}
              </div>
            </div>
            <button
              type="button"
              onClick={handleClear}
              className="text-teal-400 hover:text-teal-600"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default WinnerSelector;
